import { AnimatePresence, motion, useMotionValue, useReducedMotion, useScroll, useSpring, useTransform } from "framer-motion";
import type { MotionValue } from "framer-motion";
import {
  ArrowRight,
  Check,
  ChevronDown,
  Code2,
  Database,
  FileText,
  Github,
  Layers3,
  Lock,
  Menu,
  Network,
  Play,
  Search,
  ShieldCheck,
  Sparkles,
  Terminal,
  Users,
  X,
  Zap,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { CSSProperties, MouseEvent } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import NetworkGraph from "@/components/NetworkGraph";
import ThemeToggle from "@/components/ui/ThemeToggle";
import { isNative } from "@/native/platform";
import { hideNativeSplashAfterFirstPaint } from "@/native/appLifecycle";
import CouncilLogo from "@/components/ui/CouncilLogo";

const HLS_STREAM = "https://stream.mux.com/tLkHO1qZoaaQOUeVWo8hEBeGQfySP02EPS02BmnNFyXys.m3u8";

type AnimeLayout = {
  update: (callback: () => void, options?: Record<string, unknown>) => void;
  revert: () => void;
};

const navItems = [
  { label: "Features", href: "/features" },
  { label: "Methodology", href: "/methodology" },
  { label: "Pricing", href: "/pricing" },
  { label: "FAQ", href: "/faq" },
  { label: "See How It Works", href: "/how-it-works" },
];

const capabilityChips = ["Consultation", "Deliberation", "Investigation", "Archives", "Evidence", "Records", "API", "CLI", "MCP"];


const features = [
  {
    icon: Users,
    title: "Specialist Deliberation",
    text: "The Strategist, Scholar, Researcher, Engineer, and Analyst reason independently, identify each other's blind spots, and converge through the Chair.",
  },
  {
    icon: Database,
    title: "Archives",
    text: "Important context shouldn't have to be repeated. Workspace facts, project preferences, and long-term context persist - and stay out of the way when irrelevant.",
  },
  {
    icon: Search,
    title: "Evidence Retrieval",
    text: "Your documents are consulted before anything else. Vector, keyword, and full-text retrieval surface evidence before external sources are considered.",
  },
  {
    icon: Network,
    title: "Investigation",
    text: "Not a search. A structured inquiry. Council plans the investigation, collects evidence, detects contradictions, and writes a cited record you can actually use.",
  },
  {
    icon: FileText,
    title: "Council Records",
    text: "The output of every session stays. Decisions, tables, briefs, and structured records persist inside the workspace, linked to the evidence behind them.",
  },
  {
    icon: Terminal,
    title: "Built for Operators",
    text: "One reasoning engine. Web app, REST API, CLI, and MCP-compatible tooling - all calling the same deliberation layer.",
  },
];

const plans = [
  {
    name: "Free",
    price: "Rs 0",
    period: "/ forever",
    description: "For individuals exploring deliberation.",
    badge: "",
    features: ["10 queries / month", "Core deliberation", "Evidence-aware sessions", "7-day history"],
    cta: "Start thinking",
  },
  {
    name: "Pro",
    price: "Rs 1,999",
    period: "/month",
    description: "For people who make important decisions regularly.",
    badge: "Most deliberated choice",
    features: ["500 queries / month", "All specialists", "Investigation", "Archives", "Council Records", "API access"],
    cta: "Start deliberating",
    featured: true,
  },
  {
    name: "Business",
    price: "Rs 5,599",
    period: "/seat / month",
    description: "For teams where every decision carries weight.",
    badge: "",
    features: ["Unlimited queries", "Workspace controls", "MCP + CLI", "Team-ready architecture", "Priority deliberation support"],
    cta: "Talk to us",
  },
];

const faqs = [
  {
    q: "How does Council decide which sources to use?",
    a: "Your workspace documents are always consulted first - through vector, keyword, and full-text retrieval. External knowledge is only used when your own context doesn't have the answer. Nothing is assumed when evidence exists.",
  },
  {
    q: "Can teams use the API and CLI?",
    a: "Yes. The web app, CLI, REST API, and MCP server all call the same deliberation layer. Business plan workspaces include full access with auth, audit trails, and rate controls.",
  },
  {
    q: "Do Council Records persist between sessions?",
    a: "They do. Decisions, research briefs, tables, and structured records stay linked to the evidence that produced them, available across sessions and inspectable at any time.",
  },
  {
    q: "Does Council replace my existing AI subscriptions?",
    a: "It convenes them. Council turns raw models into specialists with distinct roles, then lets the Chair challenge and synthesize their reasoning.",
  },
];

function HeroVideo() {
  const hostRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || typeof document === "undefined") return;

    const video = document.createElement("video");
    let hls: import("hls.js").default | null = null;
    let disposed = false;
    let appended = false;

    const appendPlayingVideo = () => {
      if (disposed || appended || !host) return;
      appended = true;
      host.appendChild(video);
      requestAnimationFrame(() => {
        video.classList.add("mc-hero-video-visible");
      });
    };

    const playVideo = () => {
      if (disposed) return;
      void video.play().catch(() => undefined);
    };

    video.className = "mc-hero-video absolute inset-0 h-full w-full object-cover";
    video.controls = false;
    video.muted = true;
    video.autoplay = true;
    video.playsInline = true;
    video.defaultMuted = true;
    video.loop = true;
    video.disablePictureInPicture = true;
    video.preload = "auto";
    video.tabIndex = -1;
    video.setAttribute("aria-hidden", "true");
    video.setAttribute("muted", "true");
    video.setAttribute("playsinline", "true");
    video.setAttribute("webkit-playsinline", "true");
    video.setAttribute("disableRemotePlayback", "true");
    video.setAttribute("controlsList", "nodownload nofullscreen noremoteplayback");
    video.addEventListener("playing", appendPlayingVideo);
    video.addEventListener("canplay", playVideo);
    video.addEventListener("loadeddata", playVideo);

    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = HLS_STREAM;
      video.load();
      playVideo();
    } else {
      void import("hls.js").then(({ default: Hls }) => {
        if (disposed || !Hls.isSupported()) return;
        hls = new Hls({ enableWorker: false, autoStartLoad: true, startPosition: 0 });
        hls.loadSource(HLS_STREAM);
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, playVideo);
      });
    }

    return () => {
      disposed = true;
      video.removeEventListener("playing", appendPlayingVideo);
      video.removeEventListener("canplay", playVideo);
      video.removeEventListener("loadeddata", playVideo);
      hls?.destroy();
      video.pause();
      video.removeAttribute("src");
      video.load();
      video.remove();
    };
  }, []);

  return <div ref={hostRef} className="absolute inset-0" aria-hidden="true" />;
}
function SiteBackground({ video = false, animated = true, className = "" }: { video?: boolean; animated?: boolean; className?: string }) {
  return (
    <div className={`mc-depth-field pointer-events-none absolute inset-0 overflow-hidden ${animated ? "" : "mc-bg-static"} ${className}`} aria-hidden="true">
      <div className="absolute inset-0 bg-[#030806]" />
      <div className="mc-instant-hero-motion absolute inset-0" />
      {video ? <HeroVideo /> : null}
      <div className="absolute inset-0 bg-[linear-gradient(90deg,#030806_0%,rgba(3,8,6,0.86)_24%,rgba(3,8,6,0.22)_58%,rgba(3,8,6,0.62)_100%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(0deg,#030806_0%,rgba(3,8,6,0.92)_8%,rgba(3,8,6,0.1)_48%,rgba(3,8,6,0.28)_100%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_57%_22%,rgba(94,210,156,0.18),transparent_28%),radial-gradient(circle_at_12%_56%,rgba(34,211,238,0.075),transparent_24%)]" />
      <div className="mc-neural-haze absolute inset-[-14%]" />
      <div className="absolute inset-y-0 left-1/4 hidden w-px bg-white/[0.055] lg:block" />
      <div className="absolute inset-y-0 left-1/2 hidden w-px bg-white/[0.055] lg:block" />
      <div className="absolute inset-y-0 left-3/4 hidden w-px bg-white/[0.055] lg:block" />
      <div className="absolute inset-0 opacity-[0.08] [background-image:linear-gradient(rgba(255,255,255,.4)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.32)_1px,transparent_1px)] [background-size:120px_120px]" />
    </div>
  );
}

function CursorGlow() {
  const reduceMotion = useReducedMotion();
  const mouseX = useMotionValue(-300);
  const mouseY = useMotionValue(-300);
  const glowX = useSpring(mouseX, { stiffness: 70, damping: 24, mass: 0.4 });
  const glowY = useSpring(mouseY, { stiffness: 70, damping: 24, mass: 0.4 });
  const dotX = useSpring(mouseX, { stiffness: 440, damping: 36, mass: 0.2 });
  const dotY = useSpring(mouseY, { stiffness: 440, damping: 36, mass: 0.2 });

  useEffect(() => {
    if (reduceMotion) return;
    const onMouseMove = (event: globalThis.MouseEvent) => {
      mouseX.set(event.clientX);
      mouseY.set(event.clientY);
    };
    window.addEventListener("mousemove", onMouseMove);
    return () => window.removeEventListener("mousemove", onMouseMove);
  }, [mouseX, mouseY, reduceMotion]);

  if (reduceMotion) return null;

  return (
    <>
      <motion.div
        className="pointer-events-none fixed left-0 top-0 z-50 hidden h-[480px] w-[480px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-emerald-300/12 blur-3xl lg:block"
        style={{ x: glowX, y: glowY }}
      />
      <motion.div
        className="pointer-events-none fixed left-0 top-0 z-50 hidden h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border border-emerald-200/80 bg-emerald-300/40 shadow-[0_0_32px_rgba(94,210,156,0.58)] lg:block"
        style={{ x: dotX, y: dotY }}
      />
    </>
  );
}

function Header() {
  const [open, setOpen] = useState(false);
  const menuHistoryRef = useRef(false);

  useEffect(() => {
    if (!open || typeof window === "undefined") return;

    window.history.pushState({ councilMobileMenu: true }, "");
    menuHistoryRef.current = true;

    const handlePopState = () => {
      menuHistoryRef.current = false;
      setOpen(false);
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [open]);

  const closeMenu = () => {
    if (menuHistoryRef.current && typeof window !== "undefined" && window.history.state?.councilMobileMenu) {
      window.history.back();
      return;
    }
    menuHistoryRef.current = false;
    setOpen(false);
  };

  return (
    <>
      <header className="council-main-nav-header fixed inset-x-0 top-0 z-40 px-3 sm:px-6">
        <nav className="mc-glass-shell mx-auto flex h-[60px] max-w-[1180px] items-center justify-between rounded-full px-4">
          <Link to="/" className="flex min-h-11 items-center gap-2 focus:outline-none focus:ring-2 focus:ring-emerald-300/70 focus:ring-offset-2 focus:ring-offset-black">
            <CouncilLogo showText={false} iconClassName="h-7 w-7 rounded-full" alt="" />
            <span className="text-sm font-semibold text-white">Council</span>
          </Link>

          <div className="hidden items-center gap-8 lg:flex">
            {navItems.map((item) => (
              <Link
                key={item.label}
                to={item.href}
                className="inline-flex min-h-11 items-center text-[13px] font-medium text-white/62 transition-colors hover:text-[#5ed29c] focus:outline-none focus:ring-2 focus:ring-emerald-300/70 focus:ring-offset-2 focus:ring-offset-black"
              >
                {item.label}
              </Link>
            ))}
          </div>

          <div className="hidden items-center gap-3 lg:flex">
            <ThemeToggle />
            <Link to="/auth" className="text-[13px] font-medium text-white/58 transition-colors hover:text-white">
              Sign In
            </Link>
            <Button asChild className="h-9 rounded-full bg-[#5ed29c] px-5 text-[11px] font-extrabold uppercase tracking-[0.13em] text-[#030806] hover:bg-emerald-200">
              <Link to="/auth">Start deliberating</Link>
            </Button>
          </div>

          <button
            type="button"
            onClick={() => setOpen(true)}
            className="grid h-10 w-10 place-items-center rounded-full border border-white/10 bg-white/[0.04] text-white lg:hidden"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>
        </nav>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-[#030806]/96 px-6 py-6 backdrop-blur-xl lg:hidden"
          >
            <div className="flex items-center justify-between">
              <Link to="/" onClick={() => setOpen(false)} className="flex min-h-11 items-center gap-2">
                <CouncilLogo showText={false} iconClassName="h-8 w-8 rounded-full" alt="" />
                <span className="font-semibold text-white">Council</span>
              </Link>
              <button type="button" onClick={closeMenu} className="grid h-11 w-11 place-items-center rounded-full bg-white/10 text-white" aria-label="Close menu">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-16 grid gap-7">
              {navItems.map((item, index) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.035, duration: 0.28 }}
                >
                  <Link
                    to={item.href}
                    onClick={() => setOpen(false)}
                    className="flex min-h-14 items-center text-4xl font-semibold tracking-tight text-white"
                  >
                    {item.label}
                  </Link>
                </motion.div>
              ))}
              <Button asChild className="mt-8 h-12 rounded-full bg-[#5ed29c] text-[#030806] hover:bg-emerald-200">
                <Link to="/auth">Start deliberating</Link>
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function HeroConsole() {
  return (
    <motion.aside
      initial={{ opacity: 0, y: 24, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay: 0.12, duration: 0.62, ease: [0.16, 1, 0.3, 1] }}
      className="mc-premium-surface relative hidden min-h-[430px] w-full max-w-[440px] overflow-hidden rounded-[28px] p-7 xl:block"
    >
      <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-[#5ed29c]/20 blur-3xl" />
      <div className="absolute inset-0 bg-[#05100c]/55" />
      <div className="absolute inset-x-10 top-0 h-px bg-gradient-to-r from-transparent via-emerald-200/70 to-transparent" />
      <div className="relative flex items-center justify-between text-[10px] font-bold uppercase tracking-[0.22em] text-white/52">
        <span>[ 2025 ]</span>
        <span className="flex items-center gap-2 text-[#5ed29c]">
          <span className="h-1.5 w-1.5 rounded-full bg-[#5ed29c] shadow-[0_0_14px_rgba(94,210,156,0.9)]" />
          Reasoning through deliberation
        </span>
      </div>

      <div className="relative mt-9">
        <div className="mb-7 inline-grid h-12 w-12 place-items-center rounded-2xl border border-white/10 bg-white/[0.05]">
          <Network className="h-6 w-6 text-[#5ed29c]" />
        </div>
        <div className="mb-4 text-[10px] font-bold uppercase tracking-[0.22em] text-[#5ed29c]">Live deliberation</div>
        <h2 className="max-w-[310px] text-4xl italic leading-[0.9] text-white [font-family:'Instrument_Serif',serif]">
          The council is in session.
        </h2>
        <div className="mt-5 flex flex-wrap gap-2">
          {["Strategist", "Scholar", "Researcher"].map((model) => (
            <span key={model} className="rounded-full border border-white/10 bg-white/[0.035] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-white/70">
              {model}
            </span>
          ))}
        </div>
        <p className="mt-4 text-xs leading-5 text-white/64">
          Specialists reason independently, challenge one another, and give the Chair the material needed for a confident decision.
        </p>
      </div>

      <div className="relative mt-5 rounded-2xl border border-white/10 bg-black/36 p-4 font-mono text-[11px] leading-6 text-emerald-100/72">
        <div className="text-white/34">Live deliberation</div>
        <div className="mt-2">
          <span className="text-[#5ed29c]">&gt;</span> council.convene(&#123; evidence: true, specialists: 3, chair: true &#125;)
        </div>
      </div>
    </motion.aside>
  );
}

function Hero() {
  const reduceMotion = useReducedMotion();
  const native = isNative();

  return (
    <section id="council" className="scroll-mt-24 relative isolate min-h-[100svh] overflow-hidden bg-[#030806] px-6 pt-32 text-white sm:px-10 lg:px-16">
      <SiteBackground video />
      <div className="relative z-10 mx-auto grid min-h-[calc(100svh-8rem)] max-w-[1248px] items-center gap-16 py-16 [perspective:1400px] lg:grid-cols-[minmax(0,1fr)_440px]">
        <motion.div
          initial={native ? false : { opacity: 0, y: reduceMotion ? 0 : 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.62, ease: [0.16, 1, 0.3, 1] }}
          className="native-visible max-w-[690px] will-change-transform"
        >
          <div className="mb-8 inline-flex items-center gap-2 rounded-full border border-emerald-200/12 bg-emerald-200/[0.045] px-4 py-2 text-[11px] font-bold uppercase tracking-[0.24em] text-[#5ed29c] shadow-[0_0_40px_rgba(94,210,156,0.08)]">
            <Sparkles className="h-3.5 w-3.5" />
            Before you decide
          </div>
          <h1
            className="max-w-[720px] text-[clamp(3.15rem,7.3vw,5.55rem)] font-extrabold uppercase leading-[0.98] tracking-[-0.075em] text-white"
            style={{ fontFamily: '"Plus Jakarta Sans", Inter, ui-sans-serif, system-ui, sans-serif' }}
          >
            Decide with absolute certainty<span className="text-[#5ed29c]">.</span>
          </h1>
          <p className="mt-8 max-w-xl text-base leading-8 text-white/68 sm:text-lg">
            Before you decide, convene the Council. Specialists reason independently, challenge weak assumptions,
            and give the Chair enough evidence to deliver a decision you can trust.
          </p>
          <div className="mt-10 flex flex-col gap-3 sm:flex-row">
            <Button asChild className="mc-button-primary group h-12 rounded-full px-7 text-[12px] font-extrabold uppercase tracking-[0.13em] text-[#030806]">
              <Link to="/auth">
                Start deliberating <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
            <Button asChild variant="outline" className="mc-button-secondary h-12 rounded-full px-7 text-[12px] font-extrabold uppercase tracking-[0.13em] text-white">
              <Link to="/how-it-works">
                <Play className="mr-2 h-4 w-4" /> See how it works
              </Link>
            </Button>
          </div>
          <div className="mt-12 flex max-w-2xl flex-wrap gap-2.5">
            {capabilityChips.map((chip) => (
              <span key={chip} className="rounded-full border border-white/10 bg-black/28 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-white/62 backdrop-blur-md">
                {chip}
              </span>
            ))}
          </div>
        </motion.div>

        <HeroConsole />
      </div>
    </section>
  );
}

function FeatureCard({ feature, index }: { feature: (typeof features)[number]; index: number }) {
  const reduceMotion = useReducedMotion();
  const Icon = feature.icon;

  return (
    <motion.article
      whileHover={reduceMotion ? undefined : { y: -4 }}
      style={{ "--feature-delay": `${index * 42}ms` } as CSSProperties}
      className="feature-layout-card mc-premium-card group relative min-h-[220px] overflow-hidden rounded-[28px] p-7"
    >
      <div className="absolute -right-16 -top-16 h-44 w-44 rounded-full bg-[#5ed29c]/0 blur-3xl transition-colors duration-500 group-hover:bg-[#5ed29c]/12" />
      <div className="relative mb-10 flex items-center justify-between">
        <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/34">0{index + 1}</span>
        <span className="grid h-11 w-11 place-items-center rounded-2xl border border-white/10 bg-white/[0.035] text-[#5ed29c]">
          <Icon className="h-5 w-5" />
        </span>
      </div>
      <h3 className="relative text-xl font-semibold tracking-[-0.03em] text-white">{feature.title}</h3>
      <p className="relative mt-4 text-sm leading-7 text-white/58">{feature.text}</p>
    </motion.article>
  );
}

function FeaturesSection() {
  const reduceMotion = useReducedMotion();
  const featureLayoutRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const root = featureLayoutRef.current;
    if (!root) return;

    if (reduceMotion) {
      root.classList.add("feature-layout-expanded");
      return;
    }

    root.classList.remove("feature-layout-expanded");
    const observer = new IntersectionObserver(
      ([entry]) => {
        root.classList.toggle("feature-layout-expanded", entry.isIntersecting);
      },
      { threshold: 0.18, rootMargin: "0px 0px -12% 0px" },
    );

    observer.observe(root);
    return () => observer.disconnect();
  }, [reduceMotion]);

  return (
    <section id="features" className="scroll-mt-24 relative bg-[#030806] px-6 py-32 text-white">
      <SiteBackground animated={false} className="opacity-70" />
      <div className="relative z-10 mx-auto max-w-7xl">
        <div className="grid gap-8 lg:grid-cols-[1fr_384px] lg:items-end">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.26em] text-[#5ed29c]">Capability map</p>
            <h2
              className="mt-5 max-w-2xl text-5xl font-extrabold leading-[0.96] tracking-[-0.055em] text-white md:text-6xl"
              style={{ fontFamily: '"Plus Jakarta Sans", Inter, ui-sans-serif, system-ui, sans-serif' }}
            >
              Every important decision deserves more than one opinion.
            </h2>
          </div>
          <div className="text-sm leading-7 text-white/56">
            <p>
              Stop asking one AI. Start consulting a council. Deliberation, retrieval, Archives, and the Chair work
              together before your decision is formed.
            </p>
            <Link to="/features" className="mt-5 inline-flex min-h-11 items-center gap-2 rounded-full text-sm font-bold text-[#5ed29c] transition-colors hover:text-emerald-200 focus:outline-none focus:ring-2 focus:ring-emerald-300/70 focus:ring-offset-2 focus:ring-offset-black">
              Explore features <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>

        <div ref={featureLayoutRef} className="feature-layout-grid mt-20">
          {features.map((feature, index) => (
            <FeatureCard key={feature.title} feature={feature} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
}

function OperatingSystemSection() {
  const reduceMotion = useReducedMotion();

  return (
    <section id="methodology" className="scroll-mt-24 relative overflow-hidden bg-[#030806] px-6 py-32 text-white">
      <SiteBackground animated={false} className="opacity-80" />
      <div className="relative z-10 mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: reduceMotion ? 0 : 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.48 }}
          className="mx-auto max-w-3xl text-center"
        >
          <p className="text-[11px] font-bold uppercase tracking-[0.26em] text-[#5ed29c]">Council methodology</p>
          <h2
            className="mt-5 text-5xl font-extrabold leading-[0.96] tracking-[-0.055em] text-white md:text-6xl"
            style={{ fontFamily: '"Plus Jakarta Sans", Inter, ui-sans-serif, system-ui, sans-serif' }}
          >
            The strongest decisions are not generated. They are debated.
          </h2>
          <p className="mt-6 text-base leading-8 text-white/56">
            Council routes every question through the right context, evidence, and specialists before the Chair writes
            a single word of the decision.
          </p>
          <Link to="/methodology" className="mt-6 inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-white/10 px-5 text-sm font-bold text-white/78 transition-colors hover:border-[#5ed29c]/40 hover:text-[#5ed29c] focus:outline-none focus:ring-2 focus:ring-emerald-300/70 focus:ring-offset-2 focus:ring-offset-black">
            Read the methodology <ArrowRight className="h-4 w-4" />
          </Link>
        </motion.div>

        <div id="platform" className="mt-20 grid gap-7">
          <motion.div
            initial={{ opacity: 0, y: reduceMotion ? 0 : 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.52 }}
            className="network-frame relative mx-auto aspect-[1.48/1] w-full max-w-7xl overflow-hidden rounded-[28px] p-0 sm:aspect-[1.82/1] sm:rounded-[34px] lg:aspect-auto lg:min-h-[620px] lg:rounded-[38px]"
          >
            <NetworkGraph />
          </motion.div>

          <div className="grid gap-5 md:grid-cols-3">
            {[
              { icon: ShieldCheck, title: "Evidence before opinions.", text: "Workspace documents, Archives, and project context are ranked before any specialist reasons about your question." },
              { icon: Code2, title: "Challenge before agreement.", text: "Specialists reason independently, then cross-examine each other's arguments for gaps, contradictions, and weak assumptions." },
              { icon: Lock, title: "Decision over speed.", text: "The Chair weighs every position, resolves conflicts, assigns confidence, and produces one decision with citations attached." },
            ].map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.article
                  key={item.title}
                  initial={{ opacity: 0, x: reduceMotion ? 0 : 16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ delay: index * 0.05, duration: 0.42 }}
                  className="mc-premium-card rounded-[30px] p-7"
                >
                  <Icon className="h-6 w-6 text-[#5ed29c]" />
                  <h3 className="mt-8 text-2xl font-semibold tracking-[-0.04em] text-white">{item.title}</h3>
                  <p className="mt-4 text-sm leading-7 text-white/56">{item.text}</p>
                </motion.article>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function PricingPlanCard({
  plan,
  index,
  reduceMotion,
  scrollYProgress,
}: {
  plan: (typeof plans)[number];
  index: number;
  reduceMotion: boolean;
  scrollYProgress: MotionValue<number>;
}) {
  const smoothProgress = useSpring(scrollYProgress, { stiffness: 92, damping: 26, mass: 0.35 });
  const fromX = index === 0 ? -340 : index === 2 ? 340 : 0;
  const fromRotate = index === 0 ? -24 : index === 2 ? 24 : 12;
  const fromRotateY = index === 0 ? 34 : index === 2 ? -34 : 0;
  const finalScale = index === 1 ? 1.03 : 1;
  const x = useTransform(smoothProgress, [0.04, 0.38], [reduceMotion ? 0 : fromX, 0]);
  const y = useTransform(smoothProgress, [0.04, 0.38], [reduceMotion ? 0 : 220, 0]);
  const rotate = useTransform(smoothProgress, [0.04, 0.38], [`${reduceMotion ? 0 : fromRotate}deg`, "0deg"]);
  const rotateY = useTransform(smoothProgress, [0.04, 0.38], [`${reduceMotion ? 0 : fromRotateY}deg`, "0deg"]);
  const scale = useTransform(smoothProgress, [0.04, 0.38], [reduceMotion ? finalScale : 0.72, finalScale]);
  const opacity = useTransform(smoothProgress, [0.02, 0.24], [reduceMotion ? 1 : 0.1, 1]);

  return (
    <motion.article
      style={{ x, y, rotate, rotateY, scale, opacity, transformPerspective: 1200 }}
      whileHover={reduceMotion ? undefined : { y: -6, transition: { duration: 0.25 } }}
      className={`pricing-plan-card mc-premium-card relative flex min-h-[520px] transform-gpu flex-col overflow-hidden rounded-[28px] p-6 will-change-transform sm:min-h-[560px] sm:p-8 lg:min-h-[620px] lg:p-10 ${
        plan.featured
          ? "is-featured border-[#5ed29c]/45 bg-white/[0.045] shadow-[0_34px_120px_rgba(94,210,156,0.14)]"
          : ""
      }`}
    >
      <div className="absolute -right-16 -top-16 h-52 w-52 rounded-full bg-[#5ed29c]/10 blur-3xl" />
      {index === 0 && <span className="absolute left-12 top-8 h-3 w-3 rounded-full bg-[#5ed29c] shadow-[0_0_22px_rgba(94,210,156,0.9)]" />}
      <div className="flex items-start justify-between gap-4">
        <h3 className="text-xl font-bold tracking-[-0.03em] text-white">{plan.name}</h3>
        {plan.featured && (
          <div className="rounded-full bg-[#5ed29c]/15 px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.14em] text-[#5ed29c]">
            {plan.badge}
          </div>
        )}
      </div>
      <div className="mt-12 flex items-end gap-2">
        <span className="text-6xl font-semibold tracking-[-0.065em] text-white">{plan.price}</span>
        <span className="pb-2 text-sm text-white/52">{plan.period}</span>
      </div>
      <p className="mt-5 min-h-[3rem] text-sm leading-6 text-white/54">{plan.description}</p>
      <ul className="mt-10 grid flex-1 gap-4 pb-8">
        {plan.features.map((feature) => (
          <li key={feature} className="flex items-center gap-4 text-base text-white/72">
            <Check className="h-4 w-4 text-[#5ed29c]" />
            {feature}
          </li>
        ))}
      </ul>
      <Button asChild className={`mt-auto h-14 w-full rounded-full text-base font-bold ${plan.featured ? "bg-gradient-to-r from-[#5ed29c] to-[#3dd8ff] text-[#030806] hover:from-emerald-200 hover:to-cyan-200" : "border border-white/10 bg-white/[0.025] text-white hover:bg-white/10"}`}>
        <Link to="/auth">
          {plan.cta}
          <ArrowRight className="ml-3 h-5 w-5" />
        </Link>
      </Button>
    </motion.article>
  );
}

function PricingSection() {
  const reduceMotion = useReducedMotion();
  const pricingRef = useRef<HTMLElement | null>(null);
  const { scrollYProgress } = useScroll({ target: pricingRef, offset: ["start end", "end start"] });

  return (
    <section ref={pricingRef} id="pricing" className="scroll-mt-24 relative overflow-visible bg-[#030806] px-6 py-24 text-white lg:min-h-[170vh] lg:py-0">
      <SiteBackground animated={false} className="opacity-60" />
      <div className="relative z-10 mx-auto flex max-w-7xl flex-col justify-start pb-10 lg:sticky lg:top-24 lg:min-h-[calc(100dvh-6rem)] lg:pb-20 lg:pt-28">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-[11px] font-bold uppercase tracking-[0.26em] text-[#5ed29c]">Start thinking better</p>
          <h2
            className="mt-5 text-5xl font-extrabold leading-[0.96] tracking-[-0.055em] text-white md:text-6xl"
            style={{ fontFamily: '"Plus Jakarta Sans", Inter, ui-sans-serif, system-ui, sans-serif' }}
          >
            Better decisions should not be a premium feature.
          </h2>
          <p className="mt-6 text-base leading-8 text-white/56">
            Start with focused deliberation. Scale into persistent, team-wide reasoning.
          </p>
          <Link to="/pricing" className="mt-6 inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-white/10 px-5 text-sm font-bold text-white/78 transition-colors hover:border-[#5ed29c]/40 hover:text-[#5ed29c] focus:outline-none focus:ring-2 focus:ring-emerald-300/70 focus:ring-offset-2 focus:ring-offset-black">
            Compare plans <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {plans.map((plan, index) => (
            <PricingPlanCard
              key={plan.name}
              plan={plan}
              index={index}
              reduceMotion={Boolean(reduceMotion)}
              scrollYProgress={scrollYProgress}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function ProofSection() {
  const reduceMotion = useReducedMotion();
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const modalLayoutRef = useRef<AnimeLayout | null>(null);
  const openOperatorCardRef = useRef<HTMLButtonElement | null>(null);
  const items = [
    {
      title: "Evidence-first decisions",
      text: "Workspace documents, memories, and instructions are ranked before generation begins. Nothing is assumed. Everything is sourced.",
      duration: 520,
    },
    {
      title: "Citation-backed records",
      text: "Every decision, table, and summary stays linked to the evidence that produced it. Inspectable. Persistent. Trustworthy.",
      duration: 760,
    },
    {
      title: "Archives that earn their place",
      text: "Durable organizational context without noise. Important facts persist. Everything else stays out of the way.",
      duration: 940,
    },
    {
      title: "Parallel deliberation",
      text: "Specialists reason independently, surface blind spots, and converge. One Chair's Decision from genuine disagreement.",
      duration: 680,
    },
    {
      title: "One orchestration layer",
      text: "Web app, CLI, REST API, and MCP server all call the same deliberation backend. Consistent reasoning, everywhere.",
      duration: 860,
    },
    {
      title: "Document intelligence",
      text: "Evidence retrieval surfaces your uploaded files first. Citations stay inspectable. Council never guesses when the document exists.",
      duration: 1040,
    },
  ];

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog || reduceMotion) return;
    let cancelled = false;

    void import("animejs/layout").then(({ createLayout }) => {
      if (cancelled) return;
      modalLayoutRef.current = createLayout(dialog, {
        children: [".operator-layout-card", ".operator-layout-title", ".operator-layout-body"],
        properties: ["--operator-overlay-alpha"],
        duration: 520,
          ease: "outExpo",
      });
    });

    return () => {
      cancelled = true;
      modalLayoutRef.current?.revert();
      modalLayoutRef.current = null;
    };
  }, [reduceMotion]);

  const closeOperatorModal = () => {
    const dialog = dialogRef.current;
    const sourceCard = openOperatorCardRef.current;
    if (!dialog || !sourceCard || !dialog.open) return;

    const close = () => {
      dialog.close();
      sourceCard.classList.remove("is-open");
      sourceCard.focus();
      openOperatorCardRef.current = null;
    };

    if (reduceMotion || !modalLayoutRef.current) {
      close();
      return;
    }

    modalLayoutRef.current.update(close, { duration: 620, ease: "outExpo" });
  };

  const openOperatorModal = (event: MouseEvent<HTMLButtonElement>) => {
    const dialog = dialogRef.current;
    const sourceCard = event.currentTarget;
    if (!dialog) return;

    if (dialog.open) closeOperatorModal();

    const clone = sourceCard.cloneNode(true) as HTMLButtonElement;
    clone.classList.add("operator-modal-clone");
    clone.classList.remove("is-open");
    clone.type = "button";
    clone.addEventListener("click", closeOperatorModal, { once: true });
    dialog.innerHTML = "";
    dialog.appendChild(clone);
    openOperatorCardRef.current = sourceCard;

    const open = () => {
      dialog.showModal();
      sourceCard.classList.add("is-open");
    };

    if (reduceMotion || !modalLayoutRef.current) {
      open();
      return;
    }

    modalLayoutRef.current.update(open, {
      duration: Number(sourceCard.dataset.duration) || 760,
      ease: "outExpo",
    });
  };

  return (
    <section className="relative overflow-hidden border-y border-white/[0.055] bg-[#06100c] py-32 text-white">
      <dialog
        ref={dialogRef}
        id="operator-layout-dialog"
        onCancel={(event) => {
          event.preventDefault();
          closeOperatorModal();
        }}
        onClick={(event) => {
          if (event.target === event.currentTarget) closeOperatorModal();
        }}
      />
      <div className="mx-auto max-w-7xl px-6">
        <div className="max-w-2xl">
          <p className="text-[11px] font-bold uppercase tracking-[0.26em] text-[#5ed29c]">Operators love it</p>
          <h2
            className="mt-5 text-5xl font-extrabold leading-[0.96] tracking-[-0.055em] text-white md:text-6xl"
            style={{ fontFamily: '"Plus Jakarta Sans", Inter, ui-sans-serif, system-ui, sans-serif' }}
          >
            Intelligence that stays inside the workflow.
          </h2>
        </div>
      </div>
      <div className="operator-marquee mt-16 overflow-hidden">
        <div className="operator-marquee-track flex w-max gap-5">
          {[...items, ...items].map((item, index) => (
            <motion.button
              type="button"
              key={`${item.title}-${index}`}
              data-layout-id={`operator-card-${index}`}
              data-duration={item.duration}
              onClick={openOperatorModal}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: (index % items.length) * 0.04 }}
              className="operator-layout-card mc-premium-card min-w-[238px] max-w-[238px] rounded-[22px] p-4 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-[#5ed29c]/60 sm:min-w-[320px] sm:max-w-[320px] sm:rounded-[28px] sm:p-7"
            >
              <Zap className="h-5 w-5 text-[#5ed29c]" />
              <p data-layout-id={`operator-title-${index}`} className="operator-layout-title mt-4 text-base font-semibold leading-tight tracking-[-0.035em] text-white sm:mt-8 sm:text-xl">
                {item.title}
              </p>
              <p data-layout-id={`operator-body-${index}`} className="operator-layout-body mt-2 line-clamp-3 text-xs leading-5 text-white/58 sm:mt-4 sm:line-clamp-none sm:text-sm sm:leading-7 sm:text-white/52">
                {item.text}
              </p>
            </motion.button>
          ))}
        </div>
      </div>
    </section>
  );
}

function FaqSection() {
  const [open, setOpen] = useState(0);

  return (
    <section id="faq" className="scroll-mt-24 relative bg-[#030806] px-6 py-32 text-white">
      <div className="mx-auto max-w-3xl text-center">
        <p className="text-[11px] font-bold uppercase tracking-[0.26em] text-[#5ed29c]">Questions</p>
        <h2
          className="mt-5 text-5xl font-extrabold leading-[0.96] tracking-[-0.055em] text-white md:text-6xl"
          style={{ fontFamily: '"Plus Jakarta Sans", Inter, ui-sans-serif, system-ui, sans-serif' }}
        >
          Answers, not reassurances.
        </h2>
        <Link to="/faq" className="mt-6 inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-white/10 px-5 text-sm font-bold text-white/78 transition-colors hover:border-[#5ed29c]/40 hover:text-[#5ed29c] focus:outline-none focus:ring-2 focus:ring-emerald-300/70 focus:ring-offset-2 focus:ring-offset-black">
          Open full FAQ <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
      <div className="mx-auto mt-12 max-w-3xl space-y-3">
        {faqs.map((faq, index) => (
          <div key={faq.q} className="overflow-hidden rounded-2xl border border-white/[0.085] bg-white/[0.035] backdrop-blur-xl">
            <button
              type="button"
              onClick={() => setOpen(open === index ? -1 : index)}
              className="flex w-full items-center justify-between gap-6 px-6 py-5 text-left text-base font-semibold text-white"
            >
              {faq.q}
              <ChevronDown className={`h-5 w-5 shrink-0 text-[#5ed29c] transition-transform ${open === index ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence initial={false}>
              {open === index && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }}>
                  <p className="px-6 pb-6 text-sm leading-7 text-white/56">{faq.a}</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="relative overflow-hidden border-t border-white/[0.055] bg-[#030806] px-6 py-16 text-white">
      <div className="pointer-events-none absolute bottom-[-72px] left-1/2 -translate-x-1/2 text-[15vw] font-black uppercase leading-none tracking-[-0.08em] text-white/[0.025]">
        WORK
      </div>
      <div className="relative z-10 mx-auto grid max-w-7xl gap-12 md:grid-cols-[1.2fr_1fr_1fr]">
        <div>
          <Link to="/" className="flex min-h-11 items-center gap-2">
            <CouncilLogo showText={false} iconClassName="h-8 w-8 rounded-full" alt="" />
            <span className="font-semibold text-white">Council</span>
            <span className="rounded-full border border-white/10 px-2 py-0.5 text-[10px] text-white/46">v1.0</span>
          </Link>
          <p className="mt-6 max-w-sm text-sm leading-7 text-white/52">
            Reasoning through deliberation, not assumption. Built for the decisions that matter.
          </p>
        </div>
        <div className="grid gap-3 text-sm text-white/54">
          <Link to="/features" className="flex min-h-11 items-center hover:text-[#5ed29c]">Features</Link>
          <Link to="/methodology" className="flex min-h-11 items-center hover:text-[#5ed29c]">Methodology</Link>
          <Link to="/pricing" className="flex min-h-11 items-center hover:text-[#5ed29c]">Pricing</Link>
          <Link to="/faq" className="flex min-h-11 items-center hover:text-[#5ed29c]">FAQ</Link>
          <Link to="/privacy" className="flex min-h-11 items-center hover:text-[#5ed29c]">Privacy Policy</Link>
          <Link to="/terms" className="flex min-h-11 items-center hover:text-[#5ed29c]">Terms of Service</Link>
          <Link to="/delete-account" className="flex min-h-11 items-center hover:text-[#5ed29c]">Delete Account</Link>
          <a href="mailto:support@council.ceo" className="flex min-h-11 items-center hover:text-[#5ed29c]">Contact</a>
        </div>
        <div>
          <div className="mt-8 flex gap-3 text-white/46">
            <Github className="h-5 w-5" />
            <Layers3 className="h-5 w-5" />
            <Terminal className="h-5 w-5" />
          </div>
          <p className="mt-8 text-xs text-white/34">(c) 2026 Council. Truth emerges through deliberation.</p>
        </div>
      </div>
    </footer>
  );
}

export default function Landing() {
  useEffect(() => {
    hideNativeSplashAfterFirstPaint();
  }, []);

  const native = isNative();

  return (
    <main
      className={`min-h-screen overflow-x-hidden bg-[#030806] text-white selection:bg-[#5ed29c]/30 selection:text-white ${native ? "native-landing" : ""}`}
      style={{ fontFamily: '"Plus Jakarta Sans", Inter, ui-sans-serif, system-ui, sans-serif' }}
    >
      <CursorGlow />
      <Header />
      <Hero />
      <FeaturesSection />
      <OperatingSystemSection />
      <PricingSection />
      <ProofSection />
      <FaqSection />
      <Footer />
    </main>
  );
}
